// Compact mobile flight card matching the user's reference screenshot.
// Top tags · time row with logo · meta line · footer with Details + Select.

import { useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Suitcase, CaretRight, Phone, CheckCircle, AirplaneInFlight } from "@phosphor-icons/react";
import { AIRLINE_LOGOS, formatDuration, formatPrice } from "@/lib/api";
import { PHONE_DISPLAY, PHONE_TEL } from "@/components/PhoneCTA";

function Tag({ kind }) {
  const map = {
    BEST: "text-emerald-700",
    CHEAPEST: "text-emerald-600",
  };
  return (
    <span className={`text-[10px] tracking-[0.18em] uppercase font-bold ${map[kind] || ""}`}>
      {kind}
    </span>
  );
}

export default function MobileFlightCard({ flight, onSelect, isBest, isCheapest, tripLabel, baseline, baselineId }) {
  const [open, setOpen] = useState(false);
  const hasCarryOn = (flight.baggages || []).some((b) => /carry/i.test(b.type || ""));
  const hasChecked = (flight.baggages || []).some((b) => /checked/i.test(b.type || ""));

  // Delta-pricing mode (CheapOair-style) — shown in the return list once an
  // outbound is locked. baseline = the selected outbound's offer total.
  const isDelta = typeof baseline === "number";
  const isIncluded = isDelta && baselineId === flight.id;
  const delta = isDelta ? flight.price - baseline : 0;

  return (
    <div
      data-testid={`flight-card-${flight.id}`}
      className="bg-white border border-[#E0DDD6] rounded-xl overflow-hidden"
    >
      {(isBest || isCheapest) && (
        <div className="px-4 pt-3 flex items-center gap-3" data-testid={`tags-${flight.id}`}>
          {isBest && <Tag kind="BEST" />}
          {isCheapest && <Tag kind="CHEAPEST" />}
        </div>
      )}

      <div className="p-4">
        {/* Times row */}
        <div className="flex items-center gap-3">
          <img
            src={AIRLINE_LOGOS[flight.airline_code]}
            alt={flight.airline_name}
            className="w-8 h-8 object-contain shrink-0"
            loading="lazy"
          />
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-2">
              <span className="text-lg font-semibold text-[#1C1C1C] tabular-nums">{flight.depart_time}</span>
              <span className="flex-1 text-center text-[#666460] text-xs">
                <span className="inline-block w-12 border-t border-[#E0DDD6] align-middle mx-1" />
              </span>
              <span className="text-lg font-semibold text-[#1C1C1C] tabular-nums">{flight.arrive_time}</span>
            </div>
            <div className="flex items-baseline gap-2 text-xs text-[#666460] mt-0.5">
              <span>{flight.origin}</span>
              <span className="flex-1" />
              <span>{flight.destination}</span>
            </div>
          </div>
          <div className="text-right shrink-0">
            {isDelta ? (
              isIncluded ? (
                <>
                  <p
                    className="font-serif text-lg text-emerald-600 tracking-tight leading-none"
                    data-testid={`price-delta-${flight.id}`}
                  >
                    Included
                  </p>
                  <p className="text-[10px] text-[#666460] uppercase tracking-wider mt-1">paired</p>
                </>
              ) : (
                <>
                  <p
                    className={`font-serif text-xl tracking-tight leading-none ${
                      delta > 0 ? "text-rose-600" : "text-emerald-600"
                    }`}
                    data-testid={`price-delta-${flight.id}`}
                  >
                    {delta > 0 ? "+" : "−"}{formatPrice(Math.abs(delta))}
                  </p>
                  <p className="text-[10px] text-[#666460] uppercase tracking-wider mt-1">
                    {formatPrice(flight.price)} total
                  </p>
                </>
              )
            ) : (
              <>
                <p className="font-serif text-xl text-[#C29B62] tracking-tight leading-none">
                  {formatPrice(flight.price, flight.currency || "USD")}
                </p>
                <p className="text-[10px] text-[#666460] uppercase tracking-wider mt-1">{tripLabel}</p>
              </>
            )}
          </div>
        </div>

        {/* Meta row */}
        <div className="flex items-center justify-between mt-3 text-xs text-[#666460]">
          <span>
            {formatDuration(flight.duration_minutes)} · {flight.stops === 0 ? "Nonstop" : `${flight.stops} stop`} · {flight.airline_name}
          </span>
          <div className="flex items-center gap-2 text-[#666460]">
            {hasCarryOn && (
              <span className="inline-flex items-center" data-testid={`bag-carry-${flight.id}`}>
                <Suitcase size={14} weight="duotone" />
                <CheckCircle size={9} weight="fill" className="text-emerald-600 -ml-1 -mt-2" />
              </span>
            )}
            {hasChecked && (
              <span className="inline-flex items-center" data-testid={`bag-checked-${flight.id}`}>
                <Suitcase size={14} weight="duotone" />
                <CheckCircle size={9} weight="fill" className="text-emerald-600 -ml-1 -mt-2" />
              </span>
            )}
          </div>
        </div>

        {/* Footer row */}
        <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#F0EDE6]">
          <button
            type="button"
            onClick={() => setOpen(true)}
            data-testid={`flight-details-${flight.id}`}
            className="text-xs tracking-[0.14em] uppercase text-[#C29B62] hover:text-[#A88550] inline-flex items-center gap-1"
          >
            Flight details <CaretRight size={11} weight="bold" />
          </button>
          <button
            type="button"
            onClick={onSelect}
            data-testid={`select-${flight.id}`}
            className="bg-[#C29B62] hover:bg-[#D4AC75] text-[#0A0A0B] font-semibold text-xs tracking-[0.16em] uppercase px-5 py-2.5 transition-colors"
          >
            Select →
          </button>
        </div>
      </div>

      {/* Details sheet */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="bottom"
          className="bg-[#F7F5F0] rounded-t-2xl border-t border-[#E0DDD6] max-h-[85vh] overflow-y-auto"
        >
          <SheetHeader className="text-left mb-4">
            <SheetTitle className="font-serif text-2xl tracking-tight text-[#1C1C1C]">
              {flight.airline_name} · {flight.flight_number}
            </SheetTitle>
          </SheetHeader>
          <div className="space-y-4 text-sm text-[#1C1C1C]">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <p className="text-[10px] tracking-[0.18em] uppercase text-[#666460]">Depart</p>
                <p className="font-semibold">{flight.depart_time} · {flight.origin}</p>
              </div>
              <div className="text-right">
                <p className="text-[10px] tracking-[0.18em] uppercase text-[#666460]">Arrive</p>
                <p className="font-semibold">{flight.arrive_time} · {flight.destination}</p>
              </div>
            </div>
            <div className="bg-white border border-[#E0DDD6] p-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AirplaneInFlight size={16} className="text-[#C29B62]" weight="duotone" />
                <span>{formatDuration(flight.duration_minutes)} · {flight.stops === 0 ? "Nonstop" : `${flight.stops} stop`}</span>
              </div>
              <span className="font-serif text-xl text-[#C29B62]">{formatPrice(flight.price, flight.currency || "USD")}</span>
            </div>

            {flight.segments && flight.segments.length > 0 && (
              <div className="bg-white border border-[#E0DDD6]">
                {flight.segments.map((s, i) => (
                  <div
                    key={i}
                    className={`p-4 ${i > 0 ? "border-t border-[#E0DDD6]" : ""}`}
                  >
                    <p className="text-xs text-[#666460]">
                      {s.airline_name || s.marketing_carrier || "Operated by"}
                      {s.flight_number ? ` · ${s.flight_number}` : ""}
                    </p>
                    <p className="font-semibold mt-1">{s.origin} → {s.destination}</p>
                    <p className="text-xs text-[#666460]">{s.depart_time} – {s.arrive_time}</p>
                  </div>
                ))}
              </div>
            )}

            <div className="bg-[#0A0A0B] text-[#F7F5F0] p-4 mt-2">
              <p className="text-xs text-[#A09E9A] mb-2">Need a flexible fare, multi-city or upgrade?</p>
              <a
                href={`tel:${PHONE_TEL}`}
                className="inline-flex items-center gap-2 text-sm font-semibold text-[#C29B62]"
              >
                <Phone size={14} weight="fill" /> Call {PHONE_DISPLAY}
              </a>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
